import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useRouterState } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { PlanGate } from '@/components/upgrade/plan-gate.component';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useBillingPortalLauncher } from '@/hooks/use-billing-portal.hook';
import { toUpgradeTier, upgradeHref } from '@/lib/upgrade-link';
import { useLazyGetBillingPortalQuery } from '@/store/features/billing.feature';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  PAID_ACCESS_EVENT,
  PAID_ACCESS_RETURN_KEY,
  type PaidAccessNotice,
} from './paid-access';

export function PaidAccessDialog({
  organizationId,
}: {
  organizationId?: string;
}) {
  const [notice, setNotice] = useState<PaidAccessNotice | null>(null);
  useEffect(() => {
    const receive = (event: Event) =>
      setNotice((event as CustomEvent<PaidAccessNotice>).detail);
    window.addEventListener(PAID_ACCESS_EVENT, receive);
    return () => window.removeEventListener(PAID_ACCESS_EVENT, receive);
  }, []);
  useEffect(() => setNotice(null), [organizationId]);
  return (
    <Dialog
      open={notice !== null}
      onOpenChange={(open) => !open && setNotice(null)}
    >
      {notice &&
        (notice.feature === 'clu_writing' ? (
          <CluWritingContent
            message={notice.message}
            onClose={() => setNotice(null)}
          />
        ) : (
          <PaidAccessContent
            notice={notice}
            organizationId={organizationId ?? ''}
            onClose={() => setNotice(null)}
          />
        ))}
    </Dialog>
  );
}

/**
 * Clu writing is a plan feature, not a launch: the dialog is the upgrade card
 * every locked page shows (the Pro trial when it can start, the plans page
 * otherwise), with no launch copy.
 */
function CluWritingContent({
  message,
  onClose,
}: {
  message: string;
  onClose: () => void;
}) {
  // The card's way in is a link to the plans page, which this dialog (mounted
  // above the router outlet) outlives: it closes when the page changes.
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const openedOn = useRef(pathname);
  useEffect(() => {
    if (pathname !== openedOn.current) onClose();
  }, [pathname, onClose]);
  return (
    <DialogContent
      className='max-h-[calc(100dvh-2rem)] max-w-[600px] gap-0 overflow-y-auto border-0 bg-transparent p-0 shadow-none sm:rounded-2xl'
      aria-describedby={undefined}
    >
      <DialogTitle className='sr-only'>Upgrade to use Clu writing</DialogTitle>
      <PlanGate title={message} plan='Pro' className='p-0' />
    </DialogContent>
  );
}

function PaidAccessContent({
  notice,
  organizationId,
  onClose,
}: {
  notice: PaidAccessNotice;
  organizationId: string;
  onClose: () => void;
}) {
  const { trial, isReady, hasActivePaidStripe, refetch } = useFeatureAccess();
  const [getBillingPortal] = useLazyGetBillingPortalQuery();
  const billingTab = useRef<Window | null>(null);
  const navigateBillingTab = useCallback((destination: string) => {
    const tab = billingTab.current;
    if (!tab || tab.closed) return;
    tab.location.href = destination;
    billingTab.current = null;
  }, []);
  const { openBillingPortal, isPortalLoading } = useBillingPortalLauncher(
    organizationId,
    getBillingPortal,
    navigateBillingTab
  );
  const manageBilling = async () => {
    if (billingTab.current) return;
    // Open during the click so popup protection does not discard the tab after
    // the asynchronous, organization-fenced URL lookup. Keep Clu's draft here.
    const tab = window.open('about:blank', '_blank');
    if (!tab) {
      toast.error(
        'Allow a new tab to open billing while keeping your work here.'
      );
      return;
    }
    tab.opener = null;
    billingTab.current = tab;
    try {
      await openBillingPortal();
    } finally {
      // Lookup failure or an organization switch must not leave a blank tab.
      if (billingTab.current === tab) {
        billingTab.current = null;
        tab.close();
      }
    }
  };
  const confirmed =
    notice.pending && isReady && hasActivePaidStripe && !trial?.active;
  useEffect(() => {
    if (!notice.pending || confirmed) return;
    void refetch();
    const timer = window.setInterval(() => void refetch(), 5000);
    return () => window.clearInterval(timer);
  }, [notice.pending, confirmed, refetch]);
  return (
    <DialogContent className='max-w-md'>
      <DialogTitle>
        {confirmed
          ? 'Your paid access is confirmed'
          : notice.paymentRequired
            ? 'Your payment needs attention'
            : notice.pending
              ? 'Your payment is being confirmed'
              : notice?.requiredPlan
                ? `Start ${notice.requiredPlan === 'pro' ? 'Pro' : 'Starter'} to launch`
                : 'Continue with a paid plan'}
      </DialogTitle>
      <DialogDescription>
        {confirmed
          ? 'Your work is saved. Close this dialog and retry the action when you are ready.'
          : notice.message}
      </DialogDescription>
      <p className='text-sm text-muted-foreground'>
        Your saved draft stays in your workspace. Launch becomes available after
        payment and plan access are confirmed. Nothing launches automatically.
      </p>
      <div className='flex justify-end gap-2'>
        <Button variant='outline' onClick={onClose}>
          {confirmed ? 'Return to my work' : 'Keep editing'}
        </Button>
        {!confirmed && (notice.paymentRequired || notice.pending) && (
          <Button
            disabled={isPortalLoading}
            onClick={() => void manageBilling()}
          >
            {isPortalLoading ? 'Opening billing...' : 'Manage billing'}
          </Button>
        )}
        {!confirmed && !notice.paymentRequired && !notice.pending && (
          <Button
            onClick={() => {
              try {
                sessionStorage.setItem(
                  PAID_ACCESS_RETURN_KEY,
                  JSON.stringify({
                    organizationId,
                    path: window.location.pathname + window.location.search,
                  })
                );
              } catch {
                /* navigation remains available */
              }
              window.location.assign(
                upgradeHref({
                  plan: toUpgradeTier(notice.requiredPlan),
                  source: 'paid_access',
                })
              );
            }}
          >
            View plans
          </Button>
        )}
      </div>
    </DialogContent>
  );
}
